import axios, { AxiosRequestConfig } from "axios";
import ApiService from "./apiService";
import EncompassService from "./encompassService";
import AuthService from "./authService";
import { GetExpressionEvaluationResults, hasAutoEnableWhen, addRegexExp, isDataTypePackage, isDataTypeDeliveryType, isDataTypeOtherControl } from "../utils/expressionEvaluationHelper";
import { IPackage, IDeliveryType, IOtherControl, IExpression } from "../models/configuration/plugin/IConfiguration";
import { IEvaluatedDelivery, IEvaluatedOtherControl, IEvaluatedPackage, IExpressionValidationResult } from "../models/IExpressionValidation";

class expressionService {

    private apiService: ApiService;
    private expressionValidationEndPoint = EncompassService.getExpressionValidationEndpoint();
    constructor() {
        this.apiService = ApiService.getInstance(this.expressionValidationEndPoint as string);
    }

    /**
     * Determines which form controls should be displayed and their auto-selection behavior
     * based on configurable expressions. Uses batch processing to minimize HTTP API calls.
     * 
     * @param token - Authentication token for API calls
     * @param controlTypes - Array of packages, delivery types, or other controls to evaluate
     * @param fieldIdValueIdPairsFromOrigin - Current form field values for expression evaluation
     * @returns Promise resolving to array of evaluated control types with visibility and selection rules
     */
    public async CanShowOnForm(
        token: string | null,
        controlTypes: IPackage[] | IDeliveryType[] | IOtherControl[],
        fieldIdValueIdPairsFromOrigin: { [key: string]: string }
    ): Promise<IEvaluatedPackage[] | IEvaluatedDelivery[] | IEvaluatedOtherControl[]> {
        const initialexpressionEvaluation: IExpressionValidationResult = {
            result: [{
                error: "",
                evaluationResult: true,
                expression: ""
            }],
        };

        // Step 1: Pre-process all items concurrently and collect expressions
        const processedItems = await this.preprocessItemsAndCollectExpressions(controlTypes || [], fieldIdValueIdPairsFromOrigin);
        
        // Step 2: Make ONE HTTP call for all unique expressions (batch optimization)
        const batchValidationResult = await this.validateAllExpressionsBatch(token, processedItems);

        // Step 3: Process results using pre-computed data
        const finalResults = this.processItemsWithBatchResults(processedItems, batchValidationResult, initialexpressionEvaluation, fieldIdValueIdPairsFromOrigin);

        return finalResults.filter((exp: any): exp is IEvaluatedPackage | IEvaluatedDelivery | IEvaluatedOtherControl => 
            exp != null
        ) as IEvaluatedPackage[] | IEvaluatedDelivery[] | IEvaluatedOtherControl[];
    }

    /**
     * Pre-processes all control items concurrently to extract and transform expressions
     */
    private async preprocessItemsAndCollectExpressions(
        controlTypes: (IPackage | IDeliveryType | IOtherControl)[],
        fieldIdValueIdPairsFromOrigin: { [key: string]: string }
    ) {
        return await Promise.all(
            controlTypes.map(async (item) => {
                const enabledExpressions = this.getEnabledExpressions(item);
                const AutoSelectWhen: string = hasAutoEnableWhen(item) ? item.AutoSelectWhen : "";

                const regularExpressions: string[] = [];
                let autoSelectExpression: string | null = null;

                // Process regular expressions (for visibility rules)
                if (enabledExpressions.length > 0) {
                    const results: IExpression[] = await GetExpressionEvaluationResults(
                        enabledExpressions, 
                        fieldIdValueIdPairsFromOrigin, 
                        ""
                    );
                    regularExpressions.push(...results.map(exp => exp.ExpressionString));
                }

                // Process auto-select expression for packages (for auto-selection behavior)
                if (item && isDataTypePackage(item) && AutoSelectWhen) {
                    const updatedAutoSelect = addRegexExp(AutoSelectWhen, fieldIdValueIdPairsFromOrigin, "");
                    if (updatedAutoSelect) {
                        autoSelectExpression = updatedAutoSelect;
                    }
                }

                const allItemExpressions = [
                    ...regularExpressions,
                    ...(autoSelectExpression ? [autoSelectExpression] : [])
                ];

                return {
                    item,
                    enabledExpressions,
                    AutoSelectWhen,
                    regularExpressions,
                    autoSelectExpression,
                    allItemExpressions
                };
            })
        );
    }

    /**
     * Validates all unique expressions in a single batch HTTP call
     */
    private async validateAllExpressionsBatch(
        token: string | null, 
        processedItems: any[]
    ): Promise<IExpressionValidationResult | null> {
        // Collect all unique expressions with deduplication
        const expressionSet = new Set<string>();
        
        processedItems.forEach(({ allItemExpressions }) => {
            allItemExpressions.forEach((expr: string) => expressionSet.add(expr));
        });
        
        const uniqueExpressions = Array.from(expressionSet);

        if (uniqueExpressions.length === 0) {
            return null;
        }
        
        return await this.validateExpressions(token, uniqueExpressions);
    }

    /**
     * Processes each item using the batched validation results
     */
    private processItemsWithBatchResults(
        processedItems: any[],
        batchValidationResult: IExpressionValidationResult | null,
        initialexpressionEvaluation: IExpressionValidationResult,
        fieldIdValueIdPairsFromOrigin: { [key: string]: string }
    ) {
        return processedItems.map(({ item, enabledExpressions, AutoSelectWhen, allItemExpressions }) => {
            if (allItemExpressions.length === 0) {
                return this.defaultEvalResult(item, initialexpressionEvaluation);
            }

            try {
                // Get validation results for this item's expressions from the batch result
                const itemExpressionResults = this.extractItemValidationResults(
                    batchValidationResult, 
                    allItemExpressions
                );

                if (item && isDataTypePackage(item)) {
                    return this.processPackageItem(item, itemExpressionResults, AutoSelectWhen, fieldIdValueIdPairsFromOrigin);
                }
                else if (item && isDataTypeDeliveryType(item)) {
                    return this.processDeliveryTypeItem(item, itemExpressionResults);
                }
                else if (item && isDataTypeOtherControl(item)) {
                    return this.processOtherControlItem(item, itemExpressionResults);
                }
                else {
                    return this.defaultEvalResult(item, initialexpressionEvaluation);
                }
            }
            catch (e) {
                console.error("Error while calling CanShowOnForm:", e);
                return this.defaultEvalResult(item, initialexpressionEvaluation);
            }
        });
    }

    /**
     * Extracts enabled expressions from a control item with consistent filtering logic
     */
    private getEnabledExpressions(item: IPackage | IDeliveryType | IOtherControl): IExpression[] {
        return item?.Expressions?.filter(exp => 
            exp.Enabled && exp.ExpressionString && exp.ExpressionString.trim() !== ""
        ) || [];
    }

    /**
     * Extracts validation results for specific expressions from the batch response
     */
    private extractItemValidationResults(
        batchResult: IExpressionValidationResult | null,
        itemExpressions: string[]
    ): IExpressionValidationResult {
        if (!batchResult) {
            return {
                result: [{
                    error: "",
                    evaluationResult: true,
                    expression: ""
                }],
            };
        }

        const filteredResults = batchResult.result.filter(result => 
            itemExpressions.includes(result.expression)
        );

        return {
            result: filteredResults
        };
    }

    // Helper methods to process different item types
    private processPackageItem(item: IPackage, evalResult: IExpressionValidationResult, AutoSelectWhen: string, fieldIdValueIdPairsFromOrigin: { [key: string]: string }): IEvaluatedPackage {
        // Transform the AutoSelectWhen expression with current field values
        const updatedAutoSelect = AutoSelectWhen ? addRegexExp(AutoSelectWhen, fieldIdValueIdPairsFromOrigin, "") : "";

        // Find the validation result for the auto-selection expression
        // This determines whether the package should be auto-selected on page load
        const autoSelect = evalResult.result.find(x => x.expression === updatedAutoSelect);
        
        // Create updated package item based on auto-selection validation result
        // If auto-selection validation fails, clear AutoSelectWhen to prevent unwanted auto-selection
        const updatedItem = {
            ...item,
            AutoSelectWhen: (autoSelect?.evaluationResult ?? false) ? item.AutoSelectWhen : "",
        };

        // IMPORTANT: Compensation logic for failed auto-selection
        // If auto-selection validation failed (AutoSelectWhen was cleared), we need to ensure
        // that the auto-selection expression doesn't interfere with regular visibility expressions.
        // We create a separate copy of the evaluation results to avoid affecting shared expressions.
        if (updatedItem.AutoSelectWhen === "" && updatedAutoSelect) {
            // Check if the auto-select expression is also used as a regular visibility expression
            const regularExpressions = this.getEnabledExpressions(item).map(exp => 
                addRegexExp(exp.ExpressionString, fieldIdValueIdPairsFromOrigin, "")
            );
            
            const isSharedExpression = regularExpressions.includes(updatedAutoSelect);
            
            if (!isSharedExpression) {
                // Safe to modify: auto-select expression is not used for visibility
                evalResult.result = evalResult.result.map(res =>
                    res.expression === updatedAutoSelect ? { ...res, evaluationResult: true } : res
                );
            }
            // If it's a shared expression, leave the original evaluation result intact
            // to preserve proper visibility behavior
        }

        return {
            validConfigType: updatedItem,
            evaluationResult: evalResult
        } as IEvaluatedPackage;
    }

    private processDeliveryTypeItem(item: IDeliveryType, evalResult: IExpressionValidationResult): IEvaluatedDelivery {
        return {
            validConfigType: item,
            evaluationResult: evalResult
        } as IEvaluatedDelivery;
    }

    private processOtherControlItem(item: IOtherControl, evalResult: IExpressionValidationResult): IEvaluatedOtherControl {
        return {
            validConfigType: item,
            evaluationResult: evalResult
        } as IEvaluatedOtherControl;
    }

    private defaultEvalResult(item: IPackage | IDeliveryType | IOtherControl | null, initialexpressionEvaluation: IExpressionValidationResult) {
        if (isDataTypePackage(item)) {
            return {
                validConfigType: item,
                evaluationResult: initialexpressionEvaluation,
            } as IEvaluatedPackage;
        }
        else if (isDataTypeDeliveryType(item)) {
            return {
                validConfigType: item,
                evaluationResult: initialexpressionEvaluation,
            } as IEvaluatedDelivery;
        }
        else if (isDataTypeOtherControl(item)) {
            return {
                validConfigType: item,
                evaluationResult: initialexpressionEvaluation,
            } as IEvaluatedOtherControl;
        }
        else {
            return null;
        }
    }

    public async validateExpressions(token: string | null, expressionList: Array<string>): Promise<IExpressionValidationResult> {

        try {
            const route = "evaluate";

            if (!expressionList || expressionList?.length === 0) {
                throw new Error("Validate Expressions needs an array of expressions or atleast one single expression.");
            }
            const body = { expressions: expressionList }
            const requestBody = JSON.stringify(body);
            const config: AxiosRequestConfig = {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            }
            let response;
            if (token === null) {
                const urlRoute = `${this.expressionValidationEndPoint}/${route}?token=${encodeURIComponent((await new AuthService().getToken()).access_token as string)}`;
                response = await axios.post(urlRoute, requestBody);
            } else {
                response = await this.apiService.post(`/${route}`, requestBody, config);
            }
            return response.data as IExpressionValidationResult;
        } catch (error) {
            console.error('Error Saving Config:', error);
            throw error;
        }
    }
}

export default expressionService; 